import { CheckCircleIcon } from '@phosphor-icons/react'
import { match } from 'ts-pattern'
import { CopyableTextField } from '../../../components/input/copyable-text-field.tsx'
import * as styles from './integration-drawer.css.ts'
import {
  getWebhookHandoffSteps,
  getWebhookUrlHelperText,
  UNPROTECTED_WEBHOOK_HELPER_TEXT,
  WEBHOOK_URL_COPY_LABEL,
  WEBHOOK_URL_LABEL,
  type WebhookHandoffSubject,
} from './integration-webhook.ts'

export type WebhookHandoffProps = {
  /** The catalog label, e.g. "HCL AppScan" — what the success line and the toast both name. */
  toolLabel: string
  instanceName: string
  webhookUrl: string
  /** The tool, and the one fact about the saved instance its own steps turn on. */
  subject: WebhookHandoffSubject
}

/**
 * What the drawer shows after a Sonar or AppScan instance is created: the instance is saved and
 * already usable for pulls, and what remains is a configuration the customer performs in a tool
 * Pixee does not administer.
 */
export const WebhookHandoff: React.FC<WebhookHandoffProps> = ({ toolLabel, instanceName, webhookUrl, subject }) => {
  const steps = getWebhookHandoffSteps(subject)
  const isWebhookUnprotected = match(subject)
    .with({ type: 'sonar' }, ({ hasWebhookSecret }) => !hasWebhookSecret)
    // An AppScan instance always authenticates its webhook one way or the other, so it is never
    // unprotected.
    .with({ type: 'appscan' }, () => false)
    .exhaustive()

  return (
    <div className={styles.section}>
      <p className={styles.handoffSuccessLine}>
        <CheckCircleIcon size={20} weight="fill" className={styles.handoffSuccessIcon} />
        <span>
          <strong>
            {toolLabel} {instanceName}
          </strong>{' '}
          is connected. Finish setup in {toolLabel} so it can send results to Pixee.
        </span>
      </p>
      <CopyableTextField
        id="webhook-url"
        label={WEBHOOK_URL_LABEL}
        value={webhookUrl}
        copyLabel={WEBHOOK_URL_COPY_LABEL}
        helperText={getWebhookUrlHelperText(toolLabel)}
        secondaryHelperText={isWebhookUnprotected ? UNPROTECTED_WEBHOOK_HELPER_TEXT : undefined}
      />
      <ol className={styles.handoffSteps}>
        {steps.map(step => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </div>
  )
}
